import * as React from 'react'
import { Dispatch, PropsWithChildren, SetStateAction, useEffect, useMemo, useState } from 'react'
import { Button, Input, makeStyles, mergeClasses, tokens, Tooltip } from '@fluentui/react-components'
import {
    ArrowMaximize24Filled,
    ArrowMaximize24Regular,
    ArrowMinimize24Filled,
    ArrowMinimize24Regular,
    bundleIcon,
    DismissFilled,
    DismissRegular,
    DismissSquareFilled,
    HatGraduationFilled,
    HatGraduationRegular,
    SearchRegular,
} from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'
import { BaseOverviewCount, BaseOverviewProps, useOverviewStyles } from './BaseOverview'
import { isOfficeInitialized } from '../../helpers/outlookHelper'
import { isAppContext } from '../../helpers/appHelper'
import { AppContext, EntityDto } from '../../config/appConfig'
import { BaseOverviewSortingType, BaseSortMenu } from './BaseSortMenu'
import { Slot } from '@fluentui/react-utilities'
import { DateTime } from 'luxon'
import { UAParser } from 'ua-parser-js'

const DismissIcon = bundleIcon(DismissFilled, DismissRegular)
const DismissSquareIcon = bundleIcon(DismissSquareFilled, DismissSquareFilled)
const FilterCollapseIcon = bundleIcon(ArrowMinimize24Filled, ArrowMinimize24Regular)
const FilterExpandIcon = bundleIcon(ArrowMaximize24Filled, ArrowMaximize24Regular)
export const SearchHintIcon = bundleIcon(HatGraduationFilled, HatGraduationRegular)

const useTooltipStyles = makeStyles({
    inverted: {
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralForegroundInverted,
    },
})

export enum ViewType {
    ListView = 'LIST_VIEW',
    KanbanView = 'KANBAN_VIEW',
}

export type BaseOverviewFilterProps<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
    HeaderT extends BaseOverviewCount = BaseOverviewCount,
    ActionButtonPropsT = any,
> = BaseOverviewProps<EntityT, FilterT, SortingT, HeaderT, ActionButtonPropsT> &
    ActionButtonPropsT &
    PropsWithChildren<{
        filtersOpen: boolean
        setFiltersOpen: (value?: boolean) => void
        sortingValues?: SortingT
        setSortingValues?: Dispatch<SetStateAction<SortingT>>
        viewportTiny: boolean
        viewportNarrow: boolean
        viewportLarge: boolean
        viewType: ViewType
        setViewType: Dispatch<SetStateAction<ViewType>>
    }>

export type BaseOverviewFilterType = {
    query?: string
    tag?: string
    icon?: Slot<'span'>
    title?: string
}

export type BaseIntervalOverviewFilterType = BaseOverviewFilterType & {
    from?: string
    to?: string
}

export function generateFromDate(tag: string): string {
    switch (tag) {
        case 'monday':
            const monday = DateTime.local().startOf('week').set({ weekday: 1 })
            return (monday > DateTime.local() ? monday.minus({ week: 1 }) : monday).toISODate()
        case 'tuesday':
            const tuesday = DateTime.local().startOf('week').set({ weekday: 2 })
            return (tuesday > DateTime.local() ? tuesday.minus({ week: 1 }) : tuesday).toISODate()
        case 'wednesday':
            const wednesday = DateTime.local().startOf('week').set({ weekday: 3 })
            return (wednesday > DateTime.local() ? wednesday.minus({ week: 1 }) : wednesday).toISODate()
        case 'thursday':
            const thursday = DateTime.local().startOf('week').set({ weekday: 4 })
            return (thursday > DateTime.local() ? thursday.minus({ week: 1 }) : thursday).toISODate()
        case 'friday':
            const friday = DateTime.local().startOf('week').set({ weekday: 5 })
            return (friday > DateTime.local() ? friday.minus({ week: 1 }) : friday).toISODate()
        case 'saturday':
            const saturday = DateTime.local().startOf('week').set({ weekday: 6 })
            return (saturday > DateTime.local() ? saturday.minus({ week: 1 }) : saturday).toISODate()
        case 'sunday':
            const sunday = DateTime.local().startOf('week').set({ weekday: 7 })
            return (sunday > DateTime.local() ? sunday.minus({ week: 1 }) : sunday).toISODate()
        case 'day':
        case 'today':
        case '7days':
            return DateTime.local().toISODate()
        case 'lastweek':
            return DateTime.local().startOf('week').minus({ week: 1 }).toISODate()
        case 'week':
            return DateTime.local().startOf('week').toISODate()
        case 'month':
            return DateTime.local().startOf('month').toISODate()
        case 'lastquarter':
            return DateTime.local().startOf('month').minus({ month: 3 }).toISODate()
        case 'lastyear':
            return DateTime.local().startOf('year').minus({ year: 1 }).toISODate()
        case 'year':
            return DateTime.local().startOf('year').toISODate()
        case 'future':
            return DateTime.local().toISODate()
        default:
            return null
    }
}

export function generateToDate(tag: string): string {
    switch (tag) {
        case 'monday':
            const monday = DateTime.local().startOf('week').set({ weekday: 1 })
            return (monday > DateTime.local() ? monday.minus({ week: 1 }) : monday).toISODate()
        case 'tuesday':
            const tuesday = DateTime.local().startOf('week').set({ weekday: 2 })
            return (tuesday > DateTime.local() ? tuesday.minus({ week: 1 }) : tuesday).toISODate()
        case 'wednesday':
            const wednesday = DateTime.local().startOf('week').set({ weekday: 3 })
            return (wednesday > DateTime.local() ? wednesday.minus({ week: 1 }) : wednesday).toISODate()
        case 'thursday':
            const thursday = DateTime.local().startOf('week').set({ weekday: 4 })
            return (thursday > DateTime.local() ? thursday.minus({ week: 1 }) : thursday).toISODate()
        case 'friday':
            const friday = DateTime.local().startOf('week').set({ weekday: 5 })
            return (friday > DateTime.local() ? friday.minus({ week: 1 }) : friday).toISODate()
        case 'saturday':
            const saturday = DateTime.local().startOf('week').set({ weekday: 6 })
            return (saturday > DateTime.local() ? saturday.minus({ week: 1 }) : saturday).toISODate()
        case 'sunday':
            const sunday = DateTime.local().startOf('week').set({ weekday: 7 })
            return (sunday > DateTime.local() ? sunday.minus({ week: 1 }) : sunday).toISODate()
        case 'day':
        case 'history':
        case 'today':
        case 'lastquarter':
        case 'lastyear':
            return DateTime.local().toISODate()
        case 'lastweek':
            return DateTime.local().endOf('week').minus({ week: 1 }).toISODate()
        case 'week':
            return DateTime.local().endOf('week').toISODate()
        case 'month':
            return DateTime.local().endOf('month').toISODate()

        case 'year':
            return DateTime.local().endOf('year').toISODate()
        case '7days':
            return DateTime.local().plus({ day: 6 }).toISODate()
        default:
            return null
    }
}

//https://stackoverflow.com/questions/66263176/sorting-object-properties-typing-errors
function sortProperties<T>(obj: T): T {
    return (Object.keys(obj) as Array<keyof T>).sort().reduce(<K extends keyof T>(accum: T, key: K) => {
        accum[key] = obj[key]
        return accum
    }, {} as T)
}

export default function BaseOverviewFilter<
    EntityT extends EntityDto,
    FilterT extends BaseOverviewFilterType,
    SortingT extends BaseOverviewSortingType<any>,
>(props: BaseOverviewFilterProps<EntityT, FilterT, SortingT>) {
    const { values, setValues, setFieldValue } = useFormikContext<FilterT>()
    const overviewStyles = useOverviewStyles()
    const tooltipStyles = useTooltipStyles()
    const { t: translate } = useTranslation()

    const filterInputRef = React.useRef<HTMLInputElement>(null)

    // Set the focus to the SearchBox upon the first render.
    useEffect(() => {
        setTimeout(() => {
            if (
                props.focusQueryOnRender !== false &&
                document.hasFocus() &&
                (!isOfficeInitialized() || !isAppContext([AppContext.Compose])) &&
                !['mobile', 'tablet', 'wearable'].includes(new UAParser().getResult().device.type)
            ) {
                filterInputRef?.current?.focus()
            }
        }, 200)
    }, [])

    const isSettingsDefaults = useMemo(() => {
        const defaultJson = JSON.stringify(sortProperties<FilterT>(props.filterToJson?.(props.defaultFilterValues) || props.defaultFilterValues))
        const currentJson = JSON.stringify(sortProperties<FilterT>(props.filterToJson?.(values) || values))

        return defaultJson.localeCompare(currentJson) === 0
    }, [values])

    const [previousValues, setPreviousValues] = useState<FilterT>()
    const [queryValue, setQueryValue] = useState<string>(values.query)
    const [expandTooltipVisible, setExpandTooltipVisible] = useState<boolean>(false)

    useEffect(() => {
        //we've reached the minimum query length
        if (values.query?.length < 3 && queryValue?.length >= 3) {
            setPreviousValues(values)
            setValues(values => ({ ...props.fixedFilterValues, statuses: values['statuses'], query: queryValue }))
            setExpandTooltipVisible(true)
        }
        //we've left the minimum query length
        else if (values.query?.length >= 3 && queryValue?.length < 3) {
            setValues({
                ...(previousValues || props.filterFromJson?.(props.defaultFilterValues) || props.defaultFilterValues),
                query: '',
            })
            setExpandTooltipVisible(false)
        } else if (queryValue?.length >= 3) {
            setFieldValue('query', queryValue)
        }
    }, [queryValue])

    return (
        <>
            <div className={mergeClasses(overviewStyles.filterContainer, props.viewportLarge && overviewStyles.filterContainerOutlookNew)}>
                <Input
                    className={overviewStyles.filterInput}
                    autoCapitalize="off"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck="false"
                    ref={filterInputRef}
                    placeholder={translate(props.inputPlaceholderKey || 'action.search')}
                    value={queryValue}
                    onChange={(_, data) => setQueryValue(data.value)}
                    contentBefore={
                        <div className={overviewStyles.flexRow}>
                            {props.filterInputHint && <props.filterInputHint {...props} />}
                            {queryValue?.length <= 0 ? (
                                <Button size={'small'} appearance={'transparent'} icon={<SearchRegular />} />
                            ) : (
                                <Tooltip
                                    content={{
                                        children: translate('base.filter.reset.query'),
                                        className: tooltipStyles.inverted,
                                    }}
                                    withArrow
                                    relationship={'description'}
                                    positioning={'below'}
                                >
                                    <Button size={'small'} appearance={'transparent'} icon={<DismissIcon />} onClick={() => setQueryValue('')} />
                                </Tooltip>
                            )}
                        </div>
                    }
                    contentAfter={
                        <>
                            <Tooltip
                                content={{
                                    children: translate('base.filter.reset'),
                                    className: tooltipStyles.inverted,
                                }}
                                withArrow
                                relationship={'description'}
                                positioning={'below'}
                            >
                                <Button
                                    size={'small'}
                                    appearance={'transparent'}
                                    className={mergeClasses(overviewStyles.colorRed, isSettingsDefaults && overviewStyles.hidden)}
                                    icon={<DismissSquareIcon />}
                                    onClick={() => {
                                        setValues({
                                            ...(props.filterFromJson?.(props.defaultFilterValues) || props.defaultFilterValues),
                                            query: '',
                                        }).then(() => setQueryValue(''))
                                    }}
                                />
                            </Tooltip>
                            <Tooltip
                                content={{
                                    children: translate('base.filter.expand'),
                                    className: tooltipStyles.inverted,
                                }}
                                withArrow
                                relationship={'description'}
                                positioning={'below'}
                                visible={expandTooltipVisible}
                                onVisibleChange={() => setExpandTooltipVisible(!expandTooltipVisible)}
                            >
                                <Button
                                    size={'small'}
                                    appearance={'transparent'}
                                    icon={props.filtersOpen ? <FilterCollapseIcon /> : <FilterExpandIcon />}
                                    onClick={() => props.setFiltersOpen()}
                                />
                            </Tooltip>
                        </>
                    }
                />
                <div
                    className={mergeClasses(
                        overviewStyles.flexGap,
                        props.headerActionsButtons && overviewStyles.flexGapXS,
                        overviewStyles.flexJustifyBetween,
                    )}
                >
                    <BaseSortMenu {...props} sortingValues={props.sortingValues} setSortingValues={props.setSortingValues} />
                    {props.headerDefaultFilterTags && <props.headerDefaultFilterTags {...props} {...props.headerDefaultFilterTagsProps} />}
                    {props.headerActionsButtons && (
                        <div className={overviewStyles.flexGap}>
                            <props.headerActionsButtons {...props} {...props.headerActionsButtonsProps} />
                        </div>
                    )}
                </div>
            </div>

            <div
                className={mergeClasses(
                    overviewStyles.filterContainer,
                    props.viewportNarrow && overviewStyles.filterContainerOutlookNew,
                    !props.filtersOpen && overviewStyles.hidden,
                )}
            >
                <div></div>
                <div className={overviewStyles.filtersContainer}>{props.children}</div>
                <div></div>
            </div>
        </>
    )
}
